import { merge } from 'ts-deepmerge';
import { useState, useEffect } from 'react';
import { MaybeLoanID } from 'src/common/types';
import { useGetWelcomeBannerModuleDataQuery } from 'src/services/graphql/generated/graphql';
import { useUnauthorizedAccessGuard } from 'src/services/graphql/hooks/useUnauthorizedAccessGuard';
import { ServerSideModuleData } from '../../GetServerSideProps/utils/getServerSideModuleData';
import { isUUID } from '../../../../utils/validators/isUUID';
import { UIStateStore } from './utils/UIStateStore';
export const WELCOME_BANNER_WAS_CLOSED = 'zhl-mortgage-application-center-web.welcome-banner.hasBeenClosed';

const isServerSideDataSufficient = (data: Pick<ServerSideModuleData, 'WelcomeBanner'>): boolean => {
    const { homeLoansDashboardByLoanId } = data.WelcomeBanner;
    const { currentlySelectedLoan } = homeLoansDashboardByLoanId ?? {};
    const { currentlyLoggedInBorrower } = currentlySelectedLoan ?? {};
    const { attributes } = currentlyLoggedInBorrower ?? {};
    const { names } = attributes ?? {};
    return !!names;
};

export const useWelcomeBannerState = ({
    Store,
    loanID,
    serverFetchedData,
}: {
    Store: UIStateStore;
    loanID: MaybeLoanID;
    serverFetchedData: Pick<ServerSideModuleData, 'WelcomeBanner'>;
}) => {
    const { WelcomeBanner } = serverFetchedData;
    const isLoanIDInvalid = !isUUID(loanID);
    const [shouldDisplay, setShouldDisplay] = useState<boolean | null>(null);

    const { data, error } = useGetWelcomeBannerModuleDataQuery({
        variables: {
            loanID: loanID as string,
        },
        skip: isLoanIDInvalid || shouldDisplay !== true || isServerSideDataSufficient(serverFetchedData),
        fetchPolicy: 'cache-and-network',
    });
    useUnauthorizedAccessGuard(error);
    const mergedData = merge(data ?? {}, WelcomeBanner);
    const { homeLoansDashboardByLoanId } = mergedData;
    const { currentlySelectedLoan } = homeLoansDashboardByLoanId ?? {};
    const { currentlyLoggedInBorrower: currentZHLBorrower } = currentlySelectedLoan ?? {};
    const { attributes } = currentZHLBorrower ?? {};
    const { names } = attributes ?? {};
    const [name] = names ?? [];
    const { first = null } = name ?? {};
    useEffect(() => {
        if (Store.get(WELCOME_BANNER_WAS_CLOSED) === null) {
            setShouldDisplay(true);
        }
    }, [Store]);

    const onClose = () => {
        Store.put(WELCOME_BANNER_WAS_CLOSED, true);
        setShouldDisplay(false);
    };

    return {
        shouldDisplay: !!shouldDisplay,
        onClose,
        headerWelcome: first ? `Welcome ${first}!` : 'Welcome!',
    };
};
